import { useEffect, useState, useContext } from 'react'
import moment from 'moment'

import LoadingContext from '../../context/Loading/Loading'

import {
  getAttendanceSummary,
  getOrgEventsForReport,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Card from 'react-bootstrap/Card'

import { ReportActions } from '../ReportActions'
import { EventSelector } from '../EventSelector'
import ReportSummary from './ReportSummary'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { LoadingContainer } from '../LoadingContainer'
import { EmptyContainer } from '../EmptyContainer'

export default function AttendanceSummaryWrapper({ organizationUUID }) {
  const { showLoading, hideLoading, isLoading } = useContext(LoadingContext)

  const [selectedEvent, setSelectedEvent] = useState()

  const [data, setData] = useState([])

  const [events, setEvents] = useState()

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)

  const [eventDate, setEventDate] = useState()

  const [forecast, setForecast] = useState(null)

  const loadEvents = async () => {
    const res = await getOrgEventsForReport(organizationUUID)

    if (res?.status === 200) {
      setEvents(res.data)
      hideLoading()
    }
  }

  useEffect(() => {
    showLoading()
    loadEvents()
  }, [])

  useEffect(() => {
    if (!selectedEvent) return

    const loadReport = async () => {
      setIsLoadingData(true)
      try {
        const res = await getAttendanceSummary(selectedEvent)

        if (res?.status === 200) {
          setData(res.data)
          setForecast(res.data?.forecast)
        }
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    setData([])
    loadReport()
    const _event = events?.find((event) => event.uuid === selectedEvent)
    const startDate = moment(_event?.start).format('YYYY-MM-DD')
    setEventDate(startDate)
  }, [selectedEvent, refreshToken])

  const refreshReport = () => {
    refresh()
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <section className="spacer-sm" id="analytics-wrapper">
          <header className="section-header-sm">
            <div className="section-heading">
              <h1>Attendance Summary</h1>
            </div>
            <p className="section-header-desc">
              This report summarizes ticket distribution and attendance across
              all sales channels for the selected event.
            </p>
          </header>
          {events?.length > 0 ? (
            <>
              <ReportActions
                isLoading={isLoadingData}
                onRefresh={refresh}
                isRefreshing={isLoadingData}
                selected={selectedEvent}
              >
                <EventSelector
                  events={events}
                  selected={selectedEvent}
                  setSelected={setSelectedEvent}
                  isLoading={isLoadingData}
                />
              </ReportActions>
              <Results
                selectedEvent={selectedEvent}
                eventDate={eventDate}
                forecast={forecast}
                isLoading={isLoadingData}
                data={data}
                refreshReport={refreshReport}
              />
            </>
          ) : (
            <Card body>
              <EmptyContainer style="center lg">
                <p>No events created</p>
                <p className="subtitle">
                  Create an event to start tracking its ticket distribution and
                  sales.
                </p>
              </EmptyContainer>
            </Card>
          )}
        </section>
      )}
    </>
  )
}

function Results({
  isLoading,
  selectedEvent,
  data,
  eventDate,
  forecast,
  refreshReport,
}) {
  if (!selectedEvent) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>Select an event to view its ticket distribution and sales.</p>
        </EmptyContainer>
      </Card>
    )
  }
  if (isLoading) {
    return <LoadingContainer />
  }
  if (data?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No data</p>
          <p className="subtitle">
            There's currently no data for this event. Please choose another
            event.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <ReportSummary
      data={data}
      selectedEvent={selectedEvent}
      eventDate={eventDate}
      eventForcast={forecast}
      refreshReport={refreshReport}
    />
  )
}
